<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import { validateProductCreate, type ValidationIssue } from '@domain/product/validation';
  import { createProduct } from '@entities/product/store.svelte';
  import type { Product } from '@domain/product/model';
  import type { ProductCreateRequest } from '@shared/types/product';

  interface Props {
    open: boolean;
    onClose: () => void;
    onCreated: (product: Product) => void;
  }

  let { open, onClose, onCreated }: Props = $props();

  // Q-B5.1 (案 C 修正版): 1-to-1 項目は全て追加モーダルに含める。
  // 1-to-many の子レコード (解決する課題 / 資料 / 競合) は詳細画面エディタで後追い。
  let name = $state('');
  let summary = $state('');
  let roadmap = $state('');
  let improvementBacklog = $state('');
  let priceDetail = $state('');
  let talkScript = $state('');

  let submitting = $state(false);
  let issues = $state<ValidationIssue[]>([]);
  let serverError = $state<string | null>(null);

  function reset() {
    name = '';
    summary = '';
    roadmap = '';
    improvementBacklog = '';
    priceDetail = '';
    talkScript = '';
    issues = [];
    serverError = null;
  }

  function close() {
    reset();
    onClose();
  }

  function issueFor(field: keyof ProductCreateRequest): string | undefined {
    return issues.find((i) => i.field === field)?.message;
  }

  async function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;
    const payload: ProductCreateRequest = {
      name: name.trim(),
      summary: summary.trim() || null,
      roadmap: roadmap.trim() || null,
      improvement_backlog: improvementBacklog.trim() || null,
      price_detail: priceDetail.trim() || null,
      talk_script: talkScript.trim() || null,
    };
    issues = validateProductCreate(payload);
    if (issues.length > 0) return;

    submitting = true;
    serverError = null;
    try {
      const product = await createProduct(payload);
      onCreated(product);
      reset();
    } catch (err) {
      serverError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }
</script>

<Modal {open} title="自社ソリューションを追加" onClose={close}>
  <form id="product-create-form" onsubmit={handleSubmit}>
    <fieldset>
      <legend>基本情報</legend>
      <label>
        <span>自社ソリューション名 <em>*</em></span>
        <input
          bind:value={name}
          required
          autocomplete="off"
          placeholder="例: 法律事務所AI活用導入支援"
        />
        {#if issueFor('name')}
          <small class="error">{issueFor('name')}</small>
        {/if}
      </label>
      <label>
        <span>概要</span>
        <textarea
          bind:value={summary}
          rows={3}
          autocomplete="off"
          placeholder="自社ソリューションの概要を 1〜3 文で"
        ></textarea>
      </label>
      <label>
        <span>ロードマップ</span>
        <textarea
          bind:value={roadmap}
          rows={2}
          autocomplete="off"
          placeholder="今後の提供予定・拡張方針など"
        ></textarea>
      </label>
      <label>
        <span>自社ソリューション改善リスト</span>
        <textarea
          bind:value={improvementBacklog}
          rows={2}
          autocomplete="off"
          placeholder="改善予定の項目を改行区切りで"
        ></textarea>
      </label>
    </fieldset>
    <fieldset>
      <legend>価格</legend>
      <label>
        <span>価格</span>
        <textarea
          bind:value={priceDetail}
          rows={3}
          autocomplete="off"
          placeholder="例: 月額 / tiered / 10,000〜50,000円 / 初期費用込み"
        ></textarea>
      </label>
    </fieldset>
    <fieldset>
      <legend>営業支援</legend>
      <label>
        <span>トークスクリプト</span>
        <textarea
          bind:value={talkScript}
          rows={2}
          autocomplete="off"
          placeholder="提案時のトークの要点"
        ></textarea>
      </label>
      <!-- 販促資料は作成後に詳細ページの資料エディタ (product_sales_materials) で追加 (C-33)。 -->
    </fieldset>
    {#if serverError}
      <p class="server-error">{serverError}</p>
    {/if}
  </form>
  {#snippet footer()}
    <button type="button" onclick={close} disabled={submitting}>キャンセル</button>
    <button type="submit" form="product-create-form" class="primary" disabled={submitting}>
      {submitting ? '作成中…' : '作成'}
    </button>
  {/snippet}
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  fieldset {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px 12px 12px;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  legend {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #374151;
    padding: 0 4px;
  }
  label {
    display: flex;
    flex-direction: column;
    font-size: 0.875rem;
    color: #374151;
    gap: 4px;
  }
  em {
    color: #dc2626;
    font-style: normal;
  }
  input,
  textarea {
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
  }
  textarea {
    resize: vertical;
  }
  small.error {
    color: #b91c1c;
  }
  .server-error {
    color: #b91c1c;
    margin: 8px 0 0;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  button.primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
